import React, { forwardRef, ReactNode } from 'react';
import { Text as RadixText, useThemeContext } from '@radix-ui/themes';
import type { RadixTextProps } from '@/types/radix-ui';
import clsx from 'clsx';
import { resolveColor as resolveColorUtil } from '@/utils/colorUtils';

export type TextSize =
  | '3xsmall'
  | '2xsmall'
  | 'xsmall'
  | 'small'
  | 'medium'
  | 'default'
  | 'large'
  | 'xlarge'
  | '2xlarge'
  | '3xlarge'
  | 'lead'
  | 'tagline'
  | 'subtitle'
  | 'caption'
  | 'label';

export type TextColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'error'
  | 'destructive'
  | 'info'
  | 'gray'
  | 'blue'
  | 'red'
  | 'green'
  | 'teal'
  | 'textPrimary'
  | 'textSecondary'
  | 'textTertiary'
  | 'inherit'
  | 'disabled'
  | 'white';

// Create polymorphic type for TextProps
type PolymorphicTextProps<C extends React.ElementType = React.ElementType> = {
  as?: C;
  children?: ReactNode;
  size?: TextSize;
  color?: TextColor;
  className?: string;
} & Omit<
  React.ComponentPropsWithoutRef<C>,
  keyof {
    as?: C;
    children?: ReactNode;
    size?: TextSize;
    color?: TextColor;
    className?: string;
  }
> &
  Omit<RadixTextProps, 'size' | 'color' | 'as' | 'children' | 'className'>;

export type TextProps<C extends React.ElementType = React.ElementType> =
  PolymorphicTextProps<C>;

function resolveColor(
  color: TextColor,
  accentColor?: string
): {
  color: RadixTextProps['color'] | undefined;
  highContrast?: boolean;
} {
  // Handle special text colors first
  switch (color) {
    case 'textPrimary':
    case 'textSecondary':
    case 'textTertiary':
    case 'inherit':
    case 'disabled':
    case 'white':
    case 'default':
      return { color: undefined };
    case 'secondary':
      return { color: 'gray', highContrast: true };
    // Use centralized color mapping for other colors
    default:
      return {
        color: resolveColorUtil<RadixTextProps['color'] | undefined>(
          color,
          accentColor || undefined
        ),
      };
  }
}

export const Text = forwardRef<HTMLElement, TextProps>(
  (
    {
      children,
      size = 'default',
      color = 'inherit',
      className,
      as = 'p',
      ...restProps
    },
    ref
  ) => {
    const theme = useThemeContext();
    const accentColor = theme?.accentColor || 'indigo';
    const { color: resolvedColor, highContrast } = resolveColor(
      color,
      accentColor
    );

    return (
      <RadixText
        as={as}
        ref={ref}
        color={resolvedColor}
        highContrast={highContrast}
        className={clsx(
          'text',
          `text--color-${color}`,
          `text--size-${size}`,
          className
        )}
        {...restProps}
      >
        {children}
      </RadixText>
    );
  }
);
Text.displayName = 'Text';
